import { useCallback, useState, useEffect } from 'react'
import Project from 'common/project'
import _data from 'common/utils/_data'
import { MemberProfileShortType } from 'common/types/MemberProfile'

type FavouriteType = 'barristers' | 'lawyers' | 'lawfirm' | 'chambers'
interface FavouritePayload {
  lawyer_id?: string
  barrister_id?: string
  chamber_id?: string
  organisation_id?: string
}
export interface Favourite {
  id: string
  lawyer?: MemberProfileShortType
  barrister?: MemberProfileShortType
  chamber?: MemberProfileShortType
  organisation?: MemberProfileShortType
  profile?: MemberProfileShortType
}

export interface FavouriteProfile {
  id: string
  profile: MemberProfileShortType
}

interface FavouriteResponse {
  count: number
  next: string | null
  previous: string | null
  results: Favourite[]
}

export const useFavourites = () => {
  const [isFavouriteProcessing, setFavouriteProcessing] =
    useState<boolean>(false)
  const [isTypeFavouriteLoading, setTypeFavouriteLoading] =
    useState<boolean>(true)
  const [typeFavourites, setTypeFavourites] = useState<FavouriteProfile[]>([])
  const [typeFavouritePage, setTypeFavouritePage] = useState(1)
  const [hasMoreTypeFavourites, setHasMoreTypeFavourites] = useState(true)
  const [typeFavouriteCount, setTypeFavouriteCount] = useState(0)
  const getEndpointType = (type: FavouriteType) => {
    return type === 'lawfirm' || type === 'chambers' ? 'favorite' : 'favourite'
  }
  const getFavouritesByType = useCallback(
    (type: FavouriteType, sortBy = '', pageSize = 8) => {
      {
        !isTypeFavouriteLoading
      }
      {
        setTypeFavouriteLoading(true)
      }
      setTypeFavouritePage(1)
      const endpointType = getEndpointType(type)
      let url = `${Project.liveApi}legal-services/${type}/${endpointType}/?page_size=${pageSize}`
      if (sortBy !== '') {
        const sortValue = sortBy.split('_')
        url = `${url}&sort_by=${sortValue[0]}&order=${sortValue[1]}`
      }
      _data
        .get(url)
        .then((data: FavouriteResponse) => {
          setTypeFavouriteCount(data.count)
          const unifiedFavourites = data.results.map((favourite) => ({
            id: favourite.id,
            profile:
              favourite.lawyer ||
              favourite.barrister ||
              favourite.organisation ||
              favourite.chamber,
          }))
          setTypeFavourites(unifiedFavourites)
          setHasMoreTypeFavourites(!!data.next)
        })
        .catch((error) => console.error(error))
        .finally(() => {
          setTypeFavouriteLoading(false)
        })
    },
    [],
  )

  const getMoreFavouritesByType = useCallback(
    (type: FavouriteType, sortBy = '', pageSize = 8) => {
      if (!hasMoreTypeFavourites) return
      const endpointType = getEndpointType(type)
      const nextPage = typeFavouritePage + 1

      let url = `${Project.liveApi}legal-services/${type}/${endpointType}/?page_size=${pageSize}&page=${nextPage}`
      if (sortBy !== '') {
        const sortValue = sortBy.split('_')
        url = `${url}&sort_by=${sortValue[0]}&order=${sortValue[1]}`
      }
      _data.get(url).then((data: FavouriteResponse) => {
        const unifiedFavourites = data.results.map((favourite) => ({
          id: favourite.id,
          profile:
            favourite.lawyer ||
            favourite.barrister ||
            favourite.organisation ||
            favourite.chamber,
        }))

        setTypeFavourites((prev) => [...prev, ...unifiedFavourites])
        setHasMoreTypeFavourites(!!data.next)
        setTypeFavouritePage(nextPage)
      })
    },
    [typeFavourites, hasMoreTypeFavourites],
  )
  const setFavourite = useCallback(async (type: FavouriteType, id: string) => {
    if (isFavouriteProcessing) return
    setFavouriteProcessing(true)
    try {
      const payload: FavouritePayload =
        type === 'lawyers'
          ? { lawyer_id: id }
          : type === 'chambers'
          ? { chamber_id: id }
          : type === 'lawfirm'
          ? { organisation_id: id }
          : { barrister_id: id }

      const endpointType =
        type === 'lawfirm' || type === 'chambers' ? 'favorite' : 'favourite'

      const endpoint = `${Project.liveApi}legal-services/${type}/${endpointType}/`
      await _data.post(endpoint, payload)
    } catch (error) {
      throw new Error(`Error in setFavourite: ${error}`)
    } finally {
      setFavouriteProcessing(false)
    }
  }, [])

  return {
    isFavouriteProcessing,
    typeFavouriteCount,
    typeFavourites,
    isTypeFavouriteLoading,
    hasMoreTypeFavourites,
    setFavourite,
    getFavouritesByType,
    getMoreFavouritesByType,
    setTypeFavourites,
    setTypeFavouriteCount,
  }
}
